import { useState } from 'react';
import {
  browserSupportsWebAuthn,
  startAuthentication,
  startRegistration,
} from '@simplewebauthn/browser';
import { agreementsApi } from '@/modules/agreements/services/api';
import type { StepUpRecoveryPath } from '@/api/schemas/agreements.types';
import type { SigningSessionResponse } from '@/api/schemas/agreements.types';
import type { InteractiveSigningMethod } from '@/modules/agreements/subject/pages/signing-page/hooks/use-signing';

/**
 * The fresh identity check ceremony (WebAuthn step-up).
 *
 * A `fresh_strong` requirement cannot be satisfied by re-attesting the session. The signer
 * must prove possession of a passkey or security key NOW, immediately before signing.
 *
 * This ceremony is SUBJECT-FACING. It enrols and authenticates a credential for the signed-in
 * subject only. It grants NO platform authority: the credential is bound to the subject's own
 * account and is used solely to satisfy this requirement. A subject who cannot complete it is
 * offered the recovery paths the server names — enrol an authenticator, or contact an
 * administrator — and is never silently downgraded to session re-attestation.
 *
 * The challenge comes from the signing session the SERVER opened — it is never generated here.
 * A client-invented value would not resolve to any session.
 */
export function useStepUpVerification(input: {
  executionId: string;
  ensureSession: (method: InteractiveSigningMethod) => Promise<SigningSessionResponse>;
  signingMethod: InteractiveSigningMethod;
  onVerified: (verificationId: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [recoveryPath, setRecoveryPath] = useState<StepUpRecoveryPath | null>(null);
  const [verified, setVerified] = useState(false);

  const supported = browserSupportsWebAuthn();

  async function enrol(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const session = await input.ensureSession(input.signingMethod);
      const begin = await agreementsApi.beginStepUpRegistration(input.executionId, {
        challenge: session.challenge,
      });
      const response = await startRegistration({ optionsJSON: begin.options as never });
      await agreementsApi.finishStepUpRegistration(input.executionId, {
        challenge: session.challenge,
        response: response as unknown as Record<string, unknown>,
      });
      setRecoveryPath(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught : new Error(String(caught)));
    } finally {
      setBusy(false);
    }
  }

  async function authenticate(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const session = await input.ensureSession(input.signingMethod);
      const begin = await agreementsApi.beginStepUpAuthentication(input.executionId, {
        challenge: session.challenge,
      });
      setRecoveryPath(begin.recoveryPath);
      const response = await startAuthentication({ optionsJSON: begin.options as never });
      await agreementsApi.finishStepUpAuthentication(input.executionId, {
        challenge: session.challenge,
        response: response as unknown as Record<string, unknown>,
      });
      // The step-up records a strong-authentication event for the ACCOUNT. The agreement still
      // needs its OWN verification event, bound to THIS signing session and THIS manifest, before
      // the sign call will accept it. Recording it is a separate, single-use server call.
      const verification = await agreementsApi.verifySigningSession(input.executionId, {
        challenge: session.challenge,
      });
      setVerified(true);
      input.onVerified(verification.verificationId);
    } catch (caught) {
      setError(caught instanceof Error ? caught : new Error(String(caught)));
    } finally {
      setBusy(false);
    }
  }

  return { busy, error, recoveryPath, verified, supported, enrol, authenticate };
}
